import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Briefcase,
  Plus,
  Search,
  Trash2,
  Edit3,
  MapPin,
  Loader2,
  CalendarDays,
  Building2,
  Euro,
  Eye,
  X,
  Check,
  Tag,
  CheckCircle2,
  XCircle,
  Globe,
  Mail,
  Phone,
  Image,
  Package,
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { Dialog } from "../components/ui/Dialog";
import { useRequireAuth } from "../hooks/useAuth";
import {
  useServices,
  useCreateService,
  useUpdateService,
  useDeleteService,
} from "../hooks/useServices";
import { useSearchOperators } from "../hooks/usePackages";
import { AddressAutocomplete } from "../components/packages/AddressAutocomplete";
import { ArrayFieldInput } from "../components/packages/ArrayFieldInput";
import { ImageGalleryInput } from "../components/packages/ImageGalleryInput";
import { LanguageSelect } from "../components/packages/LanguageSelect";
import { CategorySelect, CATEGORIES } from "../components/packages/CategorySelect";

import type {
  Service,
  PackageItemType,
  Operator,
} from "../types/database";
import type { LucideIcon } from "lucide-react";

const itemTypeConfig: Record<
  PackageItemType,
  { label: string; color: string; icon: LucideIcon }
> = {
  event: { label: "Evento", color: "#284899", icon: CalendarDays },
  hotel: { label: "Hotel", color: "#3b82f6", icon: Building2 },
  activity: { label: "Attività", color: "#10b981", icon: MapPin },
  attraction: { label: "Attrazione", color: "#8b5cf6", icon: Eye },
  transport: { label: "Trasporto", color: "#6366f1", icon: MapPin },
  other: { label: "Altro", color: "#64748b", icon: Package },
};

type ExtendedFieldKey = "location" | "category" | "images" | "included" | "tags" | "languages" | "contacts";

const EXTENDED_FIELD_OPTIONS: { key: ExtendedFieldKey; label: string; icon: LucideIcon }[] = [
  { key: "location", label: "Località", icon: MapPin },
  { key: "category", label: "Categoria", icon: Tag },
  { key: "images", label: "Immagini", icon: Image },
  { key: "included", label: "Incluso / Non incluso", icon: CheckCircle2 },
  { key: "tags", label: "Etichette", icon: Tag },
  { key: "languages", label: "Lingue", icon: Globe },
  { key: "contacts", label: "Contatti", icon: Mail },
];

function getInitialVisibleFields(service: Partial<Service>): Set<ExtendedFieldKey> {
  const fields = new Set<ExtendedFieldKey>();
  if (service.location || service.city || service.address) fields.add("location");
  if (service.category && CATEGORIES.some(c => c.value === service.category)) fields.add("category");
  if (service.image_urls && service.image_urls.length > 0) fields.add("images");
  if ((service.included && service.included.length > 0) || (service.not_included && service.not_included.length > 0)) fields.add("included");
  if (service.tags && service.tags.length > 0) fields.add("tags");
  if (service.languages && service.languages.length > 0) fields.add("languages");
  if (service.email_contact || service.phone_contact) fields.add("contacts");
  return fields;
}

function AddDetailDropdown({
  visibleFields,
  onAdd,
}: {
  visibleFields: Set<ExtendedFieldKey>;
  onAdd: (field: ExtendedFieldKey) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const availableOptions = EXTENDED_FIELD_OPTIONS.filter((opt) => !visibleFields.has(opt.key));
  if (availableOptions.length === 0) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm font-medium text-[#284899] hover:text-[#1e3570] transition-colors"
      >
        <Plus className="h-4 w-4" />
        Aggiungi dettaglio
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 bottom-full mb-1 left-0 w-56 rounded-lg border border-border bg-card shadow-lg overflow-hidden"
          >
            {availableOptions.map((opt) => {
              const Icon = opt.icon;
              return (
                <button
                  key={opt.key}
                  type="button"
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-secondary/50 transition-colors"
                  onClick={() => {
                    onAdd(opt.key);
                    setIsOpen(false);
                  }}
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {opt.label}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function MyServices() {
  const { isLoading: authLoading } = useRequireAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    serviceId: string;
  }>({ isOpen: false, serviceId: "" });

  const { data: services = [], isLoading: servicesLoading } = useServices();
  const deleteService = useDeleteService();

  const filteredServices = useMemo(() => {
    if (!searchQuery.trim()) return services;
    const query = searchQuery.toLowerCase();
    return services.filter(
      (s) =>
        s.name.toLowerCase().includes(query) ||
        s.city?.toLowerCase().includes(query) ||
        s.category?.toLowerCase().includes(query)
    );
  }, [services, searchQuery]);

  const handleDeleteService = (id: string) => {
    setConfirmDialog({ isOpen: true, serviceId: id });
  };

  const confirmDelete = async () => {
    try {
      await deleteService.mutateAsync(confirmDialog.serviceId);
    } catch (error) {
      console.error("Error deleting service:", error);
    }
    setConfirmDialog({ isOpen: false, serviceId: "" });
  };

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-[#284899]" />
              I miei servizi
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestisci i tuoi servizi da usare negli itinerari condivisi
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Servizio
          </Button>
        </motion.div>

        {/* Search */}
        {services.length > 0 && (
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Cerca servizi per nome, città..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search className="h-4 w-4" />}
            />
          </div>
        )}

        {/* Services Grid */}
        {servicesLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : services.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Briefcase className="h-16 w-16 text-muted-foreground opacity-30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nessun servizio</h3>
              <p className="text-muted-foreground mb-4">
                Crea il tuo primo servizio per riutilizzarlo nelle pianificazioni
              </p>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Servizio
              </Button>
            </CardContent>
          </Card>
        ) : filteredServices.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground opacity-30 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Nessun risultato</h3>
              <p className="text-muted-foreground mb-4">
                Nessun servizio corrisponde alla ricerca
              </p>
              <Button variant="outline" onClick={() => setSearchQuery("")}>
                Resetta ricerca
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {filteredServices.length} servizi{filteredServices.length === 1 ? "o" : ""}
              {searchQuery && ` per "${searchQuery}"`}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredServices.map((service, i) => {
                const config = itemTypeConfig[service.item_type];
                const Icon = config.icon;
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Card className="group relative overflow-hidden transition-all duration-200 hover:scale-[1.01] hover:border-[#284899]/50 h-full flex flex-col">
                      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-[#284899] to-[#1e3570] opacity-0 group-hover:opacity-100 transition-opacity" />
                      <CardContent className="p-5 flex flex-col flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {service.image_urls && service.image_urls.length > 0 ? (
                              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-border">
                                <img
                                  src={service.image_urls[0]}
                                  alt={service.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = "none";
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: `${config.color}40` }}
                              >
                                <Icon className="h-5 w-5" style={{ color: config.color }} />
                              </div>
                            )}
                            <div className="min-w-0">
                              <h3 className="font-semibold group-hover:text-[#284899] transition-colors truncate">
                                {service.name}
                              </h3>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <Badge
                                  className="text-[10px] px-1.5 py-0"
                                  style={{ backgroundColor: `${config.color}20`, color: config.color }}
                                >
                                  {config.label}
                                </Badge>
                                {service.category && (
                                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                    {CATEGORIES.find(c => c.value === service.category)?.label || service.category}
                                  </Badge>
                                )}
                                {service.operator_id && (
                                  <Badge className="bg-emerald-500/15 text-emerald-600 text-[10px] px-1.5 py-0">
                                    Database
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                              onClick={() => setEditingService(service)}
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                              onClick={() => handleDeleteService(service.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {service.description && (
                          <p className="text-sm text-muted-foreground mt-3 line-clamp-2 leading-relaxed">
                            {service.description}
                          </p>
                        )}

                        <div className="flex-1" />

                        <div className="flex items-center flex-wrap gap-3 mt-4 text-sm text-muted-foreground">
                          {service.city && (
                            <span className="flex items-center gap-1 bg-secondary/50 px-2 py-0.5 rounded-full text-xs">
                              <MapPin className="h-3 w-3" />
                              {service.city}
                            </span>
                          )}
                          {service.price && (
                            <span className="flex items-center gap-1 bg-secondary/50 px-2 py-0.5 rounded-full text-xs">
                              <Euro className="h-3 w-3" />
                              {service.price}
                            </span>
                          )}
                          {service.duration_minutes && (
                            <span className="bg-secondary/50 px-2 py-0.5 rounded-full text-xs">
                              {service.duration_minutes} min
                            </span>
                          )}
                          {service.languages && service.languages.length > 0 && (
                            <span className="flex items-center gap-1 bg-secondary/50 px-2 py-0.5 rounded-full text-xs">
                              <Globe className="h-3 w-3" />
                              {service.languages.length}
                            </span>
                          )}
                        </div>

                        <div className="text-[10px] text-muted-foreground/60 mt-3 pt-3 border-t border-border/50">
                          Aggiornato: {format(new Date(service.updated_at), "dd/MM/yyyy HH:mm", { locale: it })}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}

        {/* Add Service Modal */}
        <ServiceFormModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          mode="create"
        />

        {/* Edit Service Modal */}
        <ServiceFormModal
          isOpen={!!editingService}
          onClose={() => setEditingService(null)}
          mode="edit"
          service={editingService}
        />

        {/* Confirm Delete */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title="Elimina Servizio"
          message="Sei sicuro di voler eliminare questo servizio? Questa azione non può essere annullata."
          confirmText="Elimina"
          variant="danger"
          onConfirm={confirmDelete}
          onCancel={() => setConfirmDialog({ isOpen: false, serviceId: "" })}
        />
      </div>
    </div>
  );
}

// ============================================
// SERVICE FORM MODAL (Add / Edit)
// ============================================

function ServiceFormModal({
  isOpen,
  onClose,
  mode,
  service,
}: {
  isOpen: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  service?: Service | null;
}) {
  const [visibleFields, setVisibleFields] = useState<Set<ExtendedFieldKey>>(new Set());
  const [formData, setFormData] = useState<Partial<Service>>({
    item_type: "event",
    name: "",
    description: "",
  });
  const [operatorSearch, setOperatorSearch] = useState("");
  const [selectedOperator, setSelectedOperator] = useState<Operator | null>(null);
  const [showOperatorResults, setShowOperatorResults] = useState(false);

  const createService = useCreateService();
  const updateService = useUpdateService();
  const { data: searchResults = [], isLoading: searchLoading } =
    useSearchOperators(operatorSearch);

  // Reset form when opened
  const handleOpen = () => {
    if (mode === "edit" && service) {
      setFormData({ ...service });
      setVisibleFields(getInitialVisibleFields(service));
    } else {
      setFormData({
        item_type: "event",
        name: "",
        description: "",
        scheduled_date: null,
        scheduled_time: null,
        duration_minutes: null,
        price: null,
        location: null,
        address: null,
        city: null,
        province: null,
        latitude: null,
        longitude: null,
        image_urls: null,
        included: null,
        not_included: null,
        tags: null,
        category: null,
        languages: null,
        email_contact: null,
        phone_contact: null,
      });
      setSelectedOperator(null);
      setOperatorSearch("");
      setVisibleFields(new Set());
      setShowOperatorResults(false);
    }
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) handleOpen();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectOperator = (operator: Operator) => {
    setSelectedOperator(operator);
    const typologyLower = operator.typology?.toLowerCase() || "";
    let itemType: PackageItemType = "activity";
    if (typologyLower.includes("hotel") || typologyLower.includes("accommodation")) {
      itemType = "hotel";
    } else if (typologyLower.includes("attraction") || typologyLower.includes("museum")) {
      itemType = "attraction";
    } else if (typologyLower.includes("transport") || typologyLower.includes("transfer")) {
      itemType = "transport";
    } else if (typologyLower.includes("event") || typologyLower.includes("festival")) {
      itemType = "event";
    }
    setFormData((prev) => ({
      ...prev,
      name: operator.name,
      operator_id: operator.id,
      item_type: itemType,
    }));
    setOperatorSearch("");
    setShowOperatorResults(false);
  };

  const handleSubmit = async () => {
    if (!formData.name?.trim()) return;

    try {
      if (mode === "edit" && service) {
        await updateService.mutateAsync({
          id: service.id,
          item_type: formData.item_type || "event",
          name: formData.name!,
          description: formData.description || null,
          scheduled_date: formData.scheduled_date || null,
          scheduled_time: formData.scheduled_time || null,
          duration_minutes: formData.duration_minutes || null,
          price: formData.price || null,
          operator_id: formData.operator_id || null,
          location: formData.location || null,
          address: formData.address || null,
          city: formData.city || null,
          province: formData.province || null,
          latitude: formData.latitude || null,
          longitude: formData.longitude || null,
          image_urls: formData.image_urls?.length ? formData.image_urls : null,
          included: formData.included?.length ? formData.included : null,
          not_included: formData.not_included?.length ? formData.not_included : null,
          tags: formData.tags?.length ? formData.tags : null,
          category: formData.category || null,
          languages: formData.languages?.length ? formData.languages : null,
          email_contact: formData.email_contact || null,
          phone_contact: formData.phone_contact || null,
        });
      } else {
        await createService.mutateAsync({
          item_type: formData.item_type || "event",
          name: formData.name!,
          description: formData.description || null,
          scheduled_date: formData.scheduled_date || null,
          scheduled_time: formData.scheduled_time || null,
          duration_minutes: formData.duration_minutes || null,
          price: formData.price || null,
          notes: null,
          metadata: selectedOperator
            ? {
                operator_city: selectedOperator.city,
                operator_region: selectedOperator.region,
                operator_typology: selectedOperator.typology,
              }
            : {},
          operator_id: selectedOperator?.id || formData.operator_id || null,
          location: formData.location || null,
          address: formData.address || null,
          city: formData.city || null,
          province: formData.province || null,
          latitude: formData.latitude || null,
          longitude: formData.longitude || null,
          image_urls: formData.image_urls?.length ? formData.image_urls : null,
          included: formData.included?.length ? formData.included : null,
          not_included: formData.not_included?.length ? formData.not_included : null,
          tags: formData.tags?.length ? formData.tags : null,
          category: formData.category || null,
          languages: formData.languages?.length ? formData.languages : null,
          email_contact: formData.email_contact || null,
          phone_contact: formData.phone_contact || null,
        });
      }
      onClose();
    } catch (error) {
      console.error("Error saving service:", error);
    }
  };

  const isPending = createService.isPending || updateService.isPending;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={mode === "edit" ? "Modifica Servizio" : "Nuovo Servizio"}
      fullscreenOnMobile
    >
      <div className="p-4 space-y-4">
        {/* Operator Search */}
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
            <Search className="h-3.5 w-3.5" />
            Cerca operatore dal database
          </label>
          <div className="relative">
            <Input
              placeholder="Cerca per nome o città..."
              value={operatorSearch}
              onChange={(e) => {
                setOperatorSearch(e.target.value);
                setShowOperatorResults(e.target.value.length >= 2);
              }}
              onFocus={() => operatorSearch.length >= 2 && setShowOperatorResults(true)}
            />
            {showOperatorResults && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-lg border border-border bg-card shadow-lg"
              >
                {searchLoading ? (
                  <div className="p-3 text-center text-sm text-muted-foreground">Ricerca...</div>
                ) : searchResults.length === 0 ? (
                  <div className="p-3 text-center text-sm text-muted-foreground">Nessun risultato</div>
                ) : (
                  searchResults.map((op) => (
                    <button
                      key={op.id}
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-secondary/50 transition-colors"
                      onClick={() => handleSelectOperator(op)}
                    >
                      <div className="font-medium text-sm truncate">{op.name}</div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {op.city} - <span className="text-[#284899]">{op.typology}</span>
                      </div>
                    </button>
                  ))
                )}
              </motion.div>
            )}
          </div>

          {selectedOperator && (
            <div className="mt-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-emerald-500" />
                  <span className="text-sm font-medium">{selectedOperator.name}</span>
                  <Badge className="bg-emerald-500/20 text-emerald-600 text-[10px]">Da Database</Badge>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => {
                    setSelectedOperator(null);
                    setFormData((prev) => ({ ...prev, name: "", operator_id: undefined }));
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                {selectedOperator.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {selectedOperator.city}
                  </span>
                )}
                {selectedOperator.typology && <span className="text-[#284899]">{selectedOperator.typology}</span>}
              </div>
            </div>
          )}
        </div>

        {/* Manual entry */}
        {!selectedOperator && (
          <>
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-sm text-muted-foreground">oppure inserisci manualmente</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <select
                value={formData.item_type || "event"}
                onChange={(e) => setFormData((prev) => ({ ...prev, item_type: e.target.value as PackageItemType }))}
                className="h-9 rounded-lg border border-border bg-card px-3 text-sm focus:border-[#284899] focus:outline-none"
              >
                {Object.entries(itemTypeConfig).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
              <Input
                placeholder="Nome servizio"
                value={formData.name || ""}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Descrizione</label>
              <textarea
                placeholder="Descrizione del servizio..."
                value={formData.description || ""}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full h-20 rounded-lg border border-border bg-background px-3 py-2 text-sm resize-none focus:border-[#284899] focus:outline-none"
              />
            </div>
          </>
        )}

        {/* Date/Time/Duration */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Data</label>
            <Input
              type="date"
              value={formData.scheduled_date || ""}
              onChange={(e) => setFormData((prev) => ({ ...prev, scheduled_date: e.target.value || null }))}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Ora inizio</label>
            <Input
              type="time"
              value={formData.scheduled_time || ""}
              onChange={(e) => setFormData((prev) => ({ ...prev, scheduled_time: e.target.value || null }))}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Durata (min)</label>
            <Input
              type="number"
              min="15"
              step="15"
              placeholder="60"
              value={formData.duration_minutes || ""}
              onChange={(e) => setFormData((prev) => ({ ...prev, duration_minutes: e.target.value ? parseInt(e.target.value) : null }))}
            />
          </div>
        </div>

        {/* Price */}
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1.5 block">Prezzo (€)</label>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={formData.price || ""}
            onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value ? parseFloat(e.target.value) : null }))}
          />
        </div>

        {/* Extended Fields */}
        <div className="pt-4 border-t border-border space-y-4">
          <AddDetailDropdown
            visibleFields={visibleFields}
            onAdd={(field) => setVisibleFields((prev) => new Set(prev).add(field))}
          />

          <AnimatePresence>
            {visibleFields.has("location") && (
              <motion.div key="location" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    Località
                  </div>
                  <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("location"); return s; }); setFormData((prev) => ({ ...prev, location: null, address: null, city: null, province: null, latitude: null, longitude: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                </div>
                <AddressAutocomplete
                  value={formData.location || ""}
                  onValueChange={(value, address) => {
                    if (address) {
                      setFormData((prev) => ({ ...prev, location: address.displayName, address: address.address, city: address.city, province: address.province, latitude: address.latitude, longitude: address.longitude }));
                    } else {
                      setFormData((prev) => ({ ...prev, location: value || null }));
                    }
                  }}
                  placeholder="Cerca indirizzo..."
                />
                {formData.city && (
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <Check className="h-3 w-3 text-emerald-500" />
                    {formData.city}{formData.province && `, ${formData.province}`}
                  </div>
                )}
              </motion.div>
            )}

            {visibleFields.has("category") && (
              <motion.div key="category" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Tag className="h-4 w-4" />
                    Categoria
                  </div>
                  <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("category"); return s; }); setFormData((prev) => ({ ...prev, category: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                </div>
                <CategorySelect
                  value={formData.category || null}
                  onChange={(value) => setFormData((prev) => ({ ...prev, category: value }))}
                />
              </motion.div>
            )}

            {visibleFields.has("images") && (
              <motion.div key="images" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Image className="h-4 w-4" />
                    Immagini
                  </div>
                  <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("images"); return s; }); setFormData((prev) => ({ ...prev, image_urls: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                </div>
                <ImageGalleryInput
                  value={formData.image_urls || []}
                  onChange={(urls) => setFormData((prev) => ({ ...prev, image_urls: urls.length > 0 ? urls : null }))}
                  maxImages={5}
                />
              </motion.div>
            )}

            {visibleFields.has("included") && (
              <motion.div key="included" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4" />
                    Incluso / Non incluso
                  </div>
                  <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("included"); return s; }); setFormData((prev) => ({ ...prev, included: null, not_included: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                      Cosa è incluso
                    </div>
                    <ArrayFieldInput
                      value={formData.included || []}
                      onChange={(items) => setFormData((prev) => ({ ...prev, included: items.length > 0 ? items : null }))}
                      placeholder="Es: Pranzo, Trasporto..."
                      badgeVariant="success"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <XCircle className="h-3.5 w-3.5 text-red-500" />
                      Cosa NON è incluso
                    </div>
                    <ArrayFieldInput
                      value={formData.not_included || []}
                      onChange={(items) => setFormData((prev) => ({ ...prev, not_included: items.length > 0 ? items : null }))}
                      placeholder="Es: Bevande, Assicurazione..."
                      badgeVariant="error"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {visibleFields.has("tags") && (
              <motion.div key="tags" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Tag className="h-4 w-4" />
                    Etichette
                  </div>
                  <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("tags"); return s; }); setFormData((prev) => ({ ...prev, tags: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                </div>
                <ArrayFieldInput
                  value={formData.tags || []}
                  onChange={(items) => setFormData((prev) => ({ ...prev, tags: items.length > 0 ? items : null }))}
                  placeholder="Es: romantico, avventura..."
                  badgeVariant="default"
                />
              </motion.div>
            )}

            {visibleFields.has("languages") && (
              <motion.div key="languages" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Globe className="h-4 w-4" />
                    Lingue supportate
                  </div>
                  <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("languages"); return s; }); setFormData((prev) => ({ ...prev, languages: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                </div>
                <LanguageSelect
                  value={formData.languages || []}
                  onChange={(langs) => setFormData((prev) => ({ ...prev, languages: langs.length > 0 ? langs : null }))}
                />
              </motion.div>
            )}

            {visibleFields.has("contacts") && (
              <motion.div key="contacts" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="space-y-3 overflow-hidden">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    Contatti
                  </div>
                  <button type="button" onClick={() => { setVisibleFields((prev) => { const s = new Set(prev); s.delete("contacts"); return s; }); setFormData((prev) => ({ ...prev, email_contact: null, phone_contact: null })); }} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input
                    icon={<Mail className="h-4 w-4" />}
                    type="email"
                    placeholder="Email contatto"
                    value={formData.email_contact || ""}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email_contact: e.target.value || null }))}
                  />
                  <Input
                    icon={<Phone className="h-4 w-4" />}
                    type="tel"
                    placeholder="Telefono contatto"
                    value={formData.phone_contact || ""}
                    onChange={(e) => setFormData((prev) => ({ ...prev, phone_contact: e.target.value || null }))}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted/10 flex gap-2 shrink-0">
        <Button variant="outline" className="flex-1" onClick={onClose}>
          Annulla
        </Button>
        <Button
          className="flex-1"
          onClick={handleSubmit}
          disabled={isPending || !formData.name?.trim()}
        >
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Salvataggio...
            </>
          ) : (
            <>
              {mode === "edit" ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              {mode === "edit" ? "Salva Modifiche" : "Crea Servizio"}
            </>
          )}
        </Button>
      </div>
    </Dialog>
  );
}
